/**
 * Phone fallback for a board (ma-inzp): the canvas' items flattened into lists
 * in canvas reading order.
 */
import type { BoardItem, Project } from './types';

/**
 * Items whose `y` is within this many canvas px of the first item of a row belong
 * to the same row, even when they are not perfectly aligned (snap grid, hand
 * placement). A project card is ~256px tall, so two cards side by side that are
 * ~100px off still read as one row.
 */
export const ROW_BAND_PX = 120;

/** Top-to-bottom in row bands, then left-to-right inside a band. Does not mutate. */
export function boardReadingOrder<T extends { x: number; y: number }>(items: readonly T[]): T[] {
  const byY = items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => a.item.y - b.item.y || a.index - b.index);

  const ordered: T[] = [];
  let band: { item: T; index: number }[] = [];
  let bandTop = 0;

  const flush = () => {
    band.sort((a, b) => a.item.x - b.item.x || a.item.y - b.item.y || a.index - b.index);
    for (const entry of band) ordered.push(entry.item);
    band = [];
  };

  for (const entry of byY) {
    if (band.length > 0 && entry.item.y - bandTop > ROW_BAND_PX) flush();
    if (band.length === 0) bandTop = entry.item.y;
    band.push(entry);
  }
  flush();

  return ordered;
}

export interface BoardListProject {
  item: BoardItem;
  project: Project;
}

export interface BoardList {
  projects: BoardListProject[];
  /** Text labels with something in them. */
  notes: BoardItem[];
}

/**
 * The lists shown on a phone. Project cards whose project isn't in `projects`
 * are left out (as on the canvas); shapes and empty labels carry no content to list.
 */
export function buildBoardList(
  items: readonly BoardItem[],
  projects: readonly Project[],
): BoardList {
  const byId = new Map(projects.map((p) => [p.id, p]));
  const ordered = boardReadingOrder(items);

  const listed: BoardListProject[] = [];
  const notes: BoardItem[] = [];
  for (const item of ordered) {
    if (item.kind === 'project_card') {
      const project = item.projectId ? byId.get(item.projectId) : undefined;
      if (project) listed.push({ item, project });
    } else if (item.kind === 'text' && item.content?.trim()) {
      notes.push(item);
    }
  }
  return { projects: listed, notes };
}

/** Below this the phone's read-only canvas starts panned to the top-left rather than fitting. */
export const MIN_READABLE_ZOOM = 0.6;
const CANVAS_MARGIN_PX = 16;

interface Bounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Where the phone's read-only canvas opens: the whole board if it fits the width at a
 * readable zoom (capped at 1), otherwise at `MIN_READABLE_ZOOM` with the first item in
 * reading order in the corner. The corner is an actual item, not the bounding box's
 * corner: on a sparse or diagonal board that corner can be empty canvas.
 */
export function initialCanvasViewport(
  nodes: readonly Bounds[],
  screenWidth: number,
): { x: number; y: number; zoom: number } {
  if (nodes.length === 0) return { x: 0, y: 0, zoom: 1 };

  const left = Math.min(...nodes.map((n) => n.x));
  const top = Math.min(...nodes.map((n) => n.y));
  const right = Math.max(...nodes.map((n) => n.x + n.width));

  const fitZoom = (screenWidth - 2 * CANVAS_MARGIN_PX) / Math.max(right - left, 1);
  if (fitZoom >= MIN_READABLE_ZOOM) {
    // Everything fits across the screen, so the bounding box's top-left shows the top row.
    const zoom = Math.min(1, fitZoom);
    return { x: CANVAS_MARGIN_PX - left * zoom, y: CANVAS_MARGIN_PX - top * zoom, zoom };
  }

  const anchor = boardReadingOrder(nodes)[0];
  return {
    x: CANVAS_MARGIN_PX - anchor.x * MIN_READABLE_ZOOM,
    y: CANVAS_MARGIN_PX - anchor.y * MIN_READABLE_ZOOM,
    zoom: MIN_READABLE_ZOOM,
  };
}
